import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail, formatDate } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";
import PortalLayout from "../../components/PortalLayout";

const KIND_LABEL = { upload: "Da caricare", sign: "Da firmare" };
const STATUS_LABEL = { missing: "Mancante", rejected: "Da ricaricare" };
const STATUS_COLOR = { missing: "var(--br-text-dim)", rejected: "#b36b6b" };

const DELIVERY_LABEL = {
  sent: "Inviata",
  delivered: "Consegnata",
  delivered_delayed: "Ritardata",
  opened: "Aperta",
  clicked: "Cliccata",
  bounced: "Rimbalzata",
  complained: "Segnalata spam",
  failed: "Fallita",
};
const DELIVERY_COLOR = {
  sent: "var(--br-text-dim)",
  delivered: "#5f8a6b",
  delivered_delayed: "#b3925f",
  opened: "#5f8a6b",
  clicked: "#5f8a6b",
  bounced: "#b36b6b",
  complained: "#b36b6b",
  failed: "#b36b6b",
};

export default function AdminKycRemindersPage() {
  const { locale } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/kyc-reminders");
      setData(res.data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Errore di caricamento.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleAuto = async () => {
    if (!data) return;
    setBusy("auto");
    try {
      const next = !data.auto_enabled;
      const res = await api.post("/admin/kyc-reminders/settings", { auto_enabled: next });
      setData((d) => ({ ...d, auto_enabled: res.data.auto_enabled }));
      setNotice(res.data.auto_enabled ? "Invio automatico settimanale attivato." : "Invio automatico disattivato.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Errore.");
    } finally {
      setBusy("");
    }
  };

  const sendOne = async (id) => {
    setBusy(id);
    setNotice("");
    try {
      const res = await api.post(`/admin/kyc-reminders/${id}/send`);
      setNotice(`Promemoria inviato a ${res.data.sent_to} (${res.data.items} voci).`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio.");
    } finally {
      setBusy("");
    }
  };

  const sendAll = async () => {
    setBusy("all");
    setNotice("");
    try {
      const res = await api.post("/admin/kyc-reminders/send-all");
      setNotice(`Promemoria inviati: ${res.data.sent}.`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio.");
    } finally {
      setBusy("");
    }
  };

  const clients = data?.clients || [];

  return (
    <PortalLayout>
      <div className="max-w-[1100px] mx-auto px-6 py-14" data-testid="admin-kyc-reminders-page">
        <div className="br-overline">Compliance</div>
        <h1 className="font-serif-display text-4xl mt-3 text-[color:var(--br-parchment)]">
          Solleciti KYC
        </h1>
        <p className="text-[0.9rem] text-[color:var(--br-text-dim)] mt-3 max-w-[680px] leading-relaxed">
          Promemoria dal team Compliance ai clienti con onboarding incompleto. L'email elenca
          solo i documenti o le firme ancora da completare, nella lingua del cliente. Il cliente
          può disiscriversi in qualsiasi momento.
        </p>
        <p className="text-[0.75rem] text-[color:var(--br-text-dim)] mt-2 max-w-[680px]" data-testid="delivery-webhook-hint">
          Lo stato di consegna (inviata · consegnata · aperta · rimbalzata) si aggiorna se su
          resend.com → Webhooks è configurato l'endpoint <span className="font-mono-fin">/api/webhooks/resend</span> del
          dominio di produzione.
        </p>

        {/* Weekly auto toggle */}
        <div
          className="mt-8 br-card px-7 py-6 flex flex-wrap items-center justify-between gap-4"
          data-testid="auto-reminder-card"
        >
          <div>
            <div className="text-[1rem] text-[color:var(--br-parchment)]">Invio automatico settimanale</div>
            <div className="text-[0.82rem] text-[color:var(--br-text-dim)] mt-1 max-w-[560px]">
              Se attivo, ogni cliente idoneo riceve al massimo un promemoria ogni 7 giorni, finché
              non completa il dossier o si disiscrive.
            </div>
          </div>
          <button
            type="button"
            onClick={toggleAuto}
            disabled={busy === "auto"}
            className={`${data?.auto_enabled ? "br-btn-primary" : "br-btn-outline"} disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid="toggle-auto-reminder"
          >
            {data?.auto_enabled ? "Attivo · Disattiva" : "Disattivato · Attiva"}
          </button>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <div className="text-[0.82rem] text-[color:var(--br-text-dim)]" data-testid="reminders-count">
            {clients.length} clienti con documenti da completare
          </div>
          <button
            type="button"
            onClick={sendAll}
            disabled={busy === "all" || clients.length === 0}
            className="br-btn-outline disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="send-all-btn"
          >
            {busy === "all" ? "Invio…" : "Invia a tutti"}
          </button>
        </div>

        {notice ? (
          <div
            className="mt-5 br-card px-6 py-4 text-[0.88rem]"
            style={{ borderLeft: "2px solid #5f8a6b", color: "var(--br-parchment)" }}
            data-testid="reminders-notice"
          >
            {notice}
          </div>
        ) : null}
        {error ? (
          <div
            className="mt-5 br-card px-6 py-4 text-[0.88rem]"
            style={{ borderLeft: "2px solid #b36b6b", color: "var(--br-parchment)" }}
            data-testid="reminders-error"
          >
            {error}
          </div>
        ) : null}

        <div className="mt-8 space-y-4">
          {loading ? (
            <div className="text-[0.9rem] text-[color:var(--br-text-dim)]" data-testid="reminders-loading">
              Caricamento…
            </div>
          ) : clients.length === 0 ? (
            <div
              className="br-card px-8 py-12 text-center text-[0.9rem] text-[color:var(--br-text-dim)]"
              data-testid="reminders-empty"
            >
              Nessun cliente con documenti mancanti. 🎉
            </div>
          ) : (
            clients.map((c) => (
              <div key={c.id} className="br-card px-7 py-6" data-testid={`reminder-client-${c.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-[1.05rem] text-[color:var(--br-parchment)]">
                      {c.name || "—"}
                      <span className="font-mono-fin text-[0.6rem] tracking-[0.18em] uppercase ml-3 text-[color:var(--br-text-dim)]">
                        {c.locale}
                      </span>
                    </div>
                    <div className="text-[0.82rem] text-[color:var(--br-text-dim)] mt-1">{c.email}</div>
                    <div className="text-[0.75rem] text-[color:var(--br-text-dim)] mt-1">
                      {c.last_sent ? `Ultimo sollecito: ${formatDate(c.last_sent, locale)}` : "Mai sollecitato"}
                    </div>
                    {c.delivery ? (
                      <div className="text-[0.75rem] mt-1.5 flex items-center gap-2" data-testid={`delivery-status-${c.id}`}>
                        <span
                          className="inline-block flex-shrink-0"
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: DELIVERY_COLOR[c.delivery.status] || "var(--br-text-dim)",
                          }}
                        />
                        <span style={{ color: DELIVERY_COLOR[c.delivery.status] || "var(--br-text-dim)" }}>
                          {DELIVERY_LABEL[c.delivery.status] || c.delivery.status}
                        </span>
                        {c.delivery.last_event_at ? (
                          <span className="text-[color:var(--br-text-dim)]">
                            · {formatDate(c.delivery.last_event_at, locale)}
                          </span>
                        ) : null}
                        {c.delivery.failure_reason ? (
                          <span className="text-[color:var(--br-text-dim)] italic truncate max-w-[280px]">
                            — {c.delivery.failure_reason}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {c.unsubscribed ? (
                      <span
                        className="font-mono-fin text-[0.58rem] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full"
                        style={{ color: "#b36b6b", border: "1px solid #b36b6b" }}
                        data-testid={`reminder-unsubscribed-${c.id}`}
                      >
                        Disiscritto
                      </span>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => sendOne(c.id)}
                      disabled={busy === c.id || c.unsubscribed}
                      className="br-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`send-reminder-${c.id}`}
                    >
                      {busy === c.id ? "Invio…" : "Invia ora"}
                    </button>
                  </div>
                </div>

                <div className="mt-5 pt-5 space-y-3" style={{ borderTop: "1px solid var(--br-line)" }}>
                  {c.missing.map((m, i) => (
                    <div key={`${m.doc_type}-${i}`} data-testid={`reminder-item-${c.id}-${m.doc_type}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-[0.9rem] text-[color:var(--br-parchment)]">{m.title}</span>
                        <span
                          className="font-mono-fin text-[0.55rem] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full"
                          style={{ color: "var(--br-bronze)", border: "1px solid var(--br-bronze)" }}
                        >
                          {KIND_LABEL[m.kind] || m.kind}
                        </span>
                        <span
                          className="font-mono-fin text-[0.55rem] tracking-[0.2em] uppercase"
                          style={{ color: STATUS_COLOR[m.status] || "var(--br-text-dim)" }}
                        >
                          {STATUS_LABEL[m.status] || m.status}
                        </span>
                      </div>
                      {m.status === "rejected" && m.comment ? (
                        <div className="text-[0.78rem] text-[color:var(--br-text-dim)] italic mt-1 max-w-[620px]">
                          Motivo: “{m.comment}”
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
